import { REVIEW_SOURCE_DEFINITIONS, type ReviewSourceId } from '@flexbyt/shared';
import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import { AppPanel } from './Panel.js';

/**
 * The connected sources, as the product actually lists them.
 *
 * ===========================================================================
 * The subject is the sources, not the reviews
 * ===========================================================================
 * This section answers "where do the reviews come from and how do they get
 * here". Putting review cards in it would answer a different question and make
 * it another wall of reviews on one page. So the visual is the source list:
 * what is connected, how many reviews each one holds, when it last checked.
 *
 * ===========================================================================
 * The paste row, which is the part that was missing
 * ===========================================================================
 * The panel used to open straight into the list of connections, with a dead
 * "Add a source" pill in the corner. That showed the *outcome* of importing
 * and never the mechanism — so a first-time reader saw six rows of numbers and
 * had no idea what they had been asked to do to get them. The commonest
 * reaction to this section in that state was the reasonable one: "connect how?"
 *
 * The row at the top is the answer, drawn as the thing it actually is: a field
 * with a Google Maps link in it. One glance says *you paste a link*, which is
 * the entire import story, and it costs eight lines. The rest of the panel then
 * reads as what happens next rather than as a list appearing from nowhere.
 *
 * It is a picture, not a control — `aria-hidden`, no input element, nothing
 * focusable. A dead field that takes a keyboard user's tab stop and then does
 * nothing is worse than a drawing that plainly is one. The real version, which
 * accepts a real listing, is on the integration pages.
 *
 * ===========================================================================
 * Two Google rows, which is the point
 * ===========================================================================
 * "Google — New York" and "Google — London" sitting one above the other is the
 * fact that a paragraph struggles to state: a source is a *listing*, not a
 * platform, and a business with three locations connects three of them. The
 * product supports this properly — `ReviewSource` deliberately carries no
 * unique index on `{ siteId, source }`, and rows are told apart by
 * `displayName` — so showing it is showing something true rather than
 * suggesting a capability.
 *
 * Someone running four restaurants recognises themselves in those two rows
 * immediately, and nothing else on the page would have told them.
 */

type Connection = {
  source: ReviewSourceId;
  /** The workspace's own name for this connection. */
  displayName: string;
  reviews: number;
  syncedAt: string;
};

/**
 * A plausible account, not a maximal one.
 *
 * Six rows: two Google listings, and one each of the rest. Enough to show the
 * multiple-listings idea and the range of platforms, few enough that the panel
 * stays a calm list rather than becoming a table somebody has to read.
 */
const CONNECTIONS: Connection[] = [
  { source: 'google', displayName: 'Google — New York', reviews: 142, syncedAt: '2 hours ago' },
  // { source: 'google', displayName: 'Google — London', reviews: 87, syncedAt: '2 hours ago' },
  { source: 'trustpilot', displayName: 'Trustpilot', reviews: 64, syncedAt: 'Yesterday' },
  { source: 'tripadvisor', displayName: 'Tripadvisor', reviews: 38, syncedAt: 'Yesterday' },
  { source: 'yelp', displayName: 'Yelp — Brooklyn', reviews: 26, syncedAt: '3 days ago' },
  { source: 'airbnb', displayName: 'Airbnb — Loft 4B', reviews: 14, syncedAt: '3 days ago' },
];

const TOTAL = CONNECTIONS.reduce((sum, entry) => sum + entry.reviews, 0);

/**
 * The example link.
 *
 * Read from the product's own source definitions rather than typed out here,
 * so the shape shown on the homepage is the shape the connect panel asks for.
 * A homepage that illustrates a URL format the product does not accept is a
 * small lie that costs somebody their first five minutes.
 */
const EXAMPLE_URL = REVIEW_SOURCE_DEFINITIONS.google.urlPlaceholder;

export function ImportSources() {
  return (
    <AppPanel
      screen="Review sources"
      meta={`${CONNECTIONS.length} connected · ${TOTAL.toLocaleString()} reviews`}
    >
      {/* ------------------------------------------------- How one gets here */}
      <div className="border-b border-ink-100 bg-white px-5 py-4">
        <p className="text-caption font-medium text-ink-700">
          Paste the link to your listing
        </p>

        <div
          aria-hidden
          className="mt-2 flex items-center gap-2.5 rounded-lg bg-ink-25 py-2.5 pr-2.5 pl-3 ring-1 ring-ink-200"
        >
          <SourceGlyph source="google" className="size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate font-mono text-caption text-ink-600">
            {EXAMPLE_URL}
          </span>
          {/*
            A drawn button rather than a `<button>`: it does nothing, and a
            control that does nothing when clicked is worse than one that
            plainly is not a control. The real field is on the source pages.
          */}
          <span className="shrink-0 rounded-md bg-accent-600 px-2.5 py-1.5 text-caption font-medium text-white">
            Connect
          </span>
        </div>

        <p className="mt-2 text-caption text-ink-500">
          That is the whole of it. ReviewsJet reads the page and keeps checking
          it for new reviews.
        </p>
      </div>

      {/* ------------------------------------------------------- What arrives */}
      <ul className="divide-y divide-ink-100">
        {CONNECTIONS.map((connection, index) => (
          <li
            key={connection.displayName}
            className={cx(
              'flex items-center gap-3.5 px-5 py-3.5',
              // The second Google row is inset a little, so the two read as two
              // listings of one platform rather than two unrelated sources.
              index === 1 && 'pl-9'
            )}
          >
            <SourceGlyph source={connection.source} className="size-5 shrink-0" />

            <div className="min-w-0 flex-1">
              <p className="truncate text-body-sm font-medium text-ink-950">
                {connection.displayName}
              </p>
              <p className="mt-0.5 text-caption text-ink-500">
                Synced {connection.syncedAt}
              </p>
            </div>

            <p className="nums shrink-0 text-body-sm text-ink-700">
              {connection.reviews}
              <span className="ml-1 text-caption text-ink-400">reviews</span>
            </p>

            <span
              aria-hidden
              className="size-1.5 shrink-0 rounded-full bg-positive-700"
            />
            <span className="sr-only">Importing normally</span>
          </li>
        ))}
      </ul>
    </AppPanel>
  );
}
